import { clamp } from './fmath';
import { Path } from './path';
import { SurfaceId } from './tuning';

/**
 * Track geometry (design.md B5.3). Greybox model for M0:
 *  - the racing surface is a closed centre-line polyline with a road width,
 *    a kerb band and a run-off band; everything beyond the run-off is wall;
 *  - car parks are extra drivable rectangles (Phoenix-style shortcuts) with
 *    parked cars inside them acting as walls;
 *  - gates (start/finish + checkpoints) must be crossed in order for a lap
 *    to count. No gate is ever placed on a section a shortcut skips.
 * Pure data + arithmetic only: it runs inside the deterministic sim.
 */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CarPark extends Rect {
  obstacles: Rect[];
}

export interface GateDef {
  seg: number;
  t: number;
}

/** Scenery around the circuit (render-only). */
export type TrackTheme = 'desert' | 'riviera' | 'park' | 'forest' | 'tropical';

export interface TrackDef {
  id: string;
  name: string;
  subtitle: string;
  /** Country / tribute line for the round card. */
  country: string;
  theme: TrackTheme;
  /** Map units → world px. */
  scale: number;
  /** Added to map coordinates before scaling (leaves a border of city). */
  offset: [number, number];
  /** Closed loop of centre-line points in map units, in driving order. */
  centerline: [number, number][];
  roadWidth: number;
  kerbWidth: number;
  runoffWidth: number;
  runoffSurface: Exclude<SurfaceId, 'wall' | 'tarmac' | 'kerb'>;
  /** gates[0] is start/finish. */
  gates: GateDef[];
  /** Car parks in map units; obstacles are generated by the def. */
  carparks: CarPark[];
  /**
   * Alternative AI routes: keep centre-line vertices up to `fromVertex`,
   * drive through `via` (map units), rejoin at `toVertex`.
   */
  shortcuts?: { fromVertex: number; toVertex: number; via: [number, number][] }[];
  /** Laps for a race (M1+). */
  raceLaps: number;
  /** Default lap record for the records board, seconds. */
  defaultRecord: number;
}

export interface Seg {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  dx: number;
  dy: number;
  len: number;
  len2: number;
  ux: number;
  uy: number;
  s0: number; // distance along the lap at the segment start
}

export interface Gate {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  /** Forward direction (unit). */
  ux: number;
  uy: number;
  /** Gate centre, for respawns. */
  cx: number;
  cy: number;
  seg: number;
}

export interface NearestHit {
  d: number;
  seg: number;
  t: number;
  cx: number;
  cy: number;
}

export interface Collision {
  x: number;
  y: number;
  /** Unit normal pointing from the wall into free space. */
  nx: number;
  ny: number;
}

export class Track {
  readonly def: TrackDef;
  readonly segs: Seg[] = [];
  readonly gates: Gate[] = [];
  readonly carparks: CarPark[] = [];
  readonly length: number;
  readonly halfRoad: number;
  readonly kerbEdge: number;
  readonly runoffEdge: number;
  readonly bounds: Rect;
  /** routes[0] = main centre line; routes[1..] = shortcut variants. */
  readonly routes: Path[] = [];
  /** Distance along the centre line of the start/finish gate. */
  readonly startS: number;

  constructor(def: TrackDef) {
    this.def = def;
    const S = def.scale;
    const [ox, oy] = def.offset;
    const pts = def.centerline.map(([x, y]) => [(x + ox) * S, (y + oy) * S] as [number, number]);
    let s = 0;
    for (let i = 0; i < pts.length; i++) {
      const [ax, ay] = pts[i];
      const [bx, by] = pts[(i + 1) % pts.length];
      const dx = bx - ax;
      const dy = by - ay;
      const len2 = dx * dx + dy * dy;
      const len = Math.sqrt(len2);
      this.segs.push({ ax, ay, bx, by, dx, dy, len, len2, ux: dx / len, uy: dy / len, s0: s });
      s += len;
    }
    this.length = s;
    this.halfRoad = def.roadWidth / 2;
    this.kerbEdge = this.halfRoad + def.kerbWidth;
    this.runoffEdge = this.halfRoad + def.runoffWidth;

    const half = this.runoffEdge + 4;
    for (const g of def.gates) {
      const sg = this.segs[g.seg];
      const cx = sg.ax + sg.dx * g.t;
      const cy = sg.ay + sg.dy * g.t;
      const nx = -sg.uy;
      const ny = sg.ux;
      this.gates.push({
        ax: cx - nx * half,
        ay: cy - ny * half,
        bx: cx + nx * half,
        by: cy + ny * half,
        ux: sg.ux,
        uy: sg.uy,
        cx,
        cy,
        seg: g.seg,
      });
    }

    for (const cp of def.carparks) {
      this.carparks.push({
        x: (cp.x + ox) * S,
        y: (cp.y + oy) * S,
        w: cp.w * S,
        h: cp.h * S,
        obstacles: cp.obstacles.map((o) => ({ x: (o.x + ox) * S, y: (o.y + oy) * S, w: o.w * S, h: o.h * S })),
      });
    }

    this.routes.push(new Path(pts));
    for (const sc of def.shortcuts ?? []) {
      const via = sc.via.map(([x, y]) => [(x + ox) * S, (y + oy) * S] as [number, number]);
      this.routes.push(new Path([...pts.slice(0, sc.fromVertex + 1), ...via, ...pts.slice(sc.toVertex)], true));
    }
    const g0 = def.gates[0];
    this.startS = this.segs[g0.seg].s0 + this.segs[g0.seg].len * g0.t;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of pts) {
      minX = Math.min(minX, x); minY = Math.min(minY, y);
      maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
    }
    const m = def.offset[0] * S;
    this.bounds = { x: minX - m, y: minY - m, w: maxX - minX + 2 * m, h: maxY - minY + 2 * m };
  }

  /** Nearest point on the centre line. */
  nearest(x: number, y: number): NearestHit {
    let best = Infinity;
    let bi = 0;
    let bt = 0;
    for (let i = 0; i < this.segs.length; i++) {
      const sg = this.segs[i];
      let t = ((x - sg.ax) * sg.dx + (y - sg.ay) * sg.dy) / sg.len2;
      t = clamp(t, 0, 1);
      const px = sg.ax + sg.dx * t - x;
      const py = sg.ay + sg.dy * t - y;
      const d2 = px * px + py * py;
      if (d2 < best) {
        best = d2;
        bi = i;
        bt = t;
      }
    }
    const sg = this.segs[bi];
    return { d: Math.sqrt(best), seg: bi, t: bt, cx: sg.ax + sg.dx * bt, cy: sg.ay + sg.dy * bt };
  }

  /** Distance along the lap of a point (projected on the centre line). */
  progressAt(x: number, y: number): number {
    const n = this.nearest(x, y);
    const sg = this.segs[n.seg];
    return sg.s0 + sg.len * n.t;
  }

  private inCarpark(x: number, y: number, r: number): boolean {
    for (const cp of this.carparks) {
      if (x < cp.x + r || x > cp.x + cp.w - r || y < cp.y + r || y > cp.y + cp.h - r) continue;
      let blocked = false;
      for (const o of cp.obstacles) {
        if (rectDist(o, x, y) < Math.max(r, 1e-9)) {
          blocked = true;
          break;
        }
      }
      if (!blocked) return true;
    }
    return false;
  }

  private onObstacle(x: number, y: number, r: number): boolean {
    for (const cp of this.carparks) {
      for (const o of cp.obstacles) if (rectDist(o, x, y) < Math.max(r, 1e-9)) return true;
    }
    return false;
  }

  surfaceAt(x: number, y: number): SurfaceId {
    const d = this.nearest(x, y).d;
    if (d <= this.halfRoad) return 'tarmac';
    if (d <= this.kerbEdge) return 'kerb';
    if (this.inCarpark(x, y, 0)) return 'carpark';
    if (d <= this.runoffEdge) return this.def.runoffSurface;
    return 'wall';
  }

  isFree(x: number, y: number, r: number): boolean {
    const n = this.nearest(x, y);
    if (n.d <= this.runoffEdge - r && !this.onObstacle(x, y, r)) return true;
    return this.inCarpark(x, y, r);
  }

  /**
   * If a circle of radius r at (x, y) overlaps a wall, return the nearest
   * free position and the wall normal; otherwise null.
   */
  collide(x: number, y: number, r: number): Collision | null {
    if (this.isFree(x, y, r)) return null;

    const cands: { x: number; y: number }[] = [];

    // 1. Back onto the road band.
    const n = this.nearest(x, y);
    const R = this.runoffEdge - r - 0.01;
    if (n.d > 1e-9) {
      const k = R / n.d;
      cands.push({ x: n.cx + (x - n.cx) * k, y: n.cy + (y - n.cy) * k });
    }

    // 2. Out of a parked car / back inside a car park.
    for (const cp of this.carparks) {
      const inner = { x: cp.x + r, y: cp.y + r, w: cp.w - 2 * r, h: cp.h - 2 * r };
      if (x < cp.x - r || x > cp.x + cp.w + r || y < cp.y - r || y > cp.y + cp.h + r) continue;
      const qx = clamp(x, inner.x, inner.x + inner.w);
      const qy = clamp(y, inner.y, inner.y + inner.h);
      cands.push({ x: qx, y: qy });
      for (const o of cp.obstacles) {
        if (rectDist(o, x, y) >= r) continue;
        const p = pushOutOfRect(o, x, y, r + 0.01);
        cands.push(p);
      }
    }

    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (const c of cands) {
      if (!this.isFree(c.x, c.y, r)) continue;
      const dx = c.x - x;
      const dy = c.y - y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD) {
        bestD = d2;
        best = c;
      }
    }
    if (!best) {
      // Corner case (e.g. wedged between a parked car and the road edge):
      // fall back to the road band, which is always free of obstacles near
      // the centre line.
      best = { x: n.cx + (x - n.cx) * ((this.halfRoad) / Math.max(n.d, 1e-9)), y: n.cy + (y - n.cy) * ((this.halfRoad) / Math.max(n.d, 1e-9)) };
      bestD = (best.x - x) * (best.x - x) + (best.y - y) * (best.y - y);
    }
    const d = Math.sqrt(bestD);
    const nx = d > 1e-9 ? (best.x - x) / d : -(x - n.cx) / Math.max(n.d, 1e-9);
    const ny = d > 1e-9 ? (best.y - y) / d : -(y - n.cy) / Math.max(n.d, 1e-9);
    return { x: best.x, y: best.y, nx, ny };
  }

  /** Did the move (x0,y0)->(x1,y1) cross gate g going forwards? */
  crossedGate(g: Gate, x0: number, y0: number, x1: number, y1: number): boolean {
    const mx = x1 - x0;
    const my = y1 - y0;
    if (mx * g.ux + my * g.uy <= 0) return false;
    return segmentsIntersect(x0, y0, x1, y1, g.ax, g.ay, g.bx, g.by);
  }
}

/** Distance from a point to a rectangle (0 inside). */
export function rectDist(o: Rect, x: number, y: number): number {
  const qx = clamp(x, o.x, o.x + o.w);
  const qy = clamp(y, o.y, o.y + o.h);
  const dx = x - qx;
  const dy = y - qy;
  return Math.sqrt(dx * dx + dy * dy);
}

function pushOutOfRect(o: Rect, x: number, y: number, r: number): { x: number; y: number } {
  const qx = clamp(x, o.x, o.x + o.w);
  const qy = clamp(y, o.y, o.y + o.h);
  const dx = x - qx;
  const dy = y - qy;
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > 1e-9) return { x: qx + (dx / d) * r, y: qy + (dy / d) * r };
  // Centre inside the rectangle: leave by the nearest side.
  const left = x - o.x;
  const right = o.x + o.w - x;
  const top = y - o.y;
  const bottom = o.y + o.h - y;
  const m = Math.min(left, right, top, bottom);
  if (m === left) return { x: o.x - r, y };
  if (m === right) return { x: o.x + o.w + r, y };
  if (m === top) return { x, y: o.y - r };
  return { x, y: o.y + o.h + r };
}

function cross(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}

export function segmentsIntersect(
  p0x: number, p0y: number, p1x: number, p1y: number,
  q0x: number, q0y: number, q1x: number, q1y: number,
): boolean {
  const rx = p1x - p0x, ry = p1y - p0y;
  const sx = q1x - q0x, sy = q1y - q0y;
  const den = cross(rx, ry, sx, sy);
  if (den === 0) return false;
  const qpx = q0x - p0x, qpy = q0y - p0y;
  const t = cross(qpx, qpy, sx, sy) / den;
  const u = cross(qpx, qpy, rx, ry) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1;
}

/** Shortest distance between two segments (render/validation helper). */
export function segSegDistance(a: Seg, b: Seg): number {
  if (segmentsIntersect(a.ax, a.ay, a.bx, a.by, b.ax, b.ay, b.bx, b.by)) return 0;
  const pd = (px: number, py: number, s: Seg) => {
    const t = clamp(((px - s.ax) * s.dx + (py - s.ay) * s.dy) / s.len2, 0, 1);
    const dx = s.ax + s.dx * t - px;
    const dy = s.ay + s.dy * t - py;
    return Math.sqrt(dx * dx + dy * dy);
  };
  return Math.min(pd(a.ax, a.ay, b), pd(a.bx, a.by, b), pd(b.ax, b.ay, a), pd(b.bx, b.by, a));
}

/**
 * Places where two different parts of the circuit come so close that their
 * run-off bands merge, AND are far enough apart round the lap that driving
 * across would be a real (accidental) shortcut. Hairpins and chicanes, whose
 * legs are close in space but also close round the lap, are fine.
 */
export function roadClashes(track: Track, margin = 6): { i: number; j: number; d: number; arc: number }[] {
  const segs = track.segs;
  const n = segs.length;
  const L = track.length;
  const need = 2 * track.runoffEdge + margin;
  const out: { i: number; j: number; d: number; arc: number }[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const fwd = segs[j].s0 - (segs[i].s0 + segs[i].len);
      const bwd = L - (segs[j].s0 + segs[j].len) + segs[i].s0;
      const arc = Math.min(fwd, bwd);
      if (arc < 4 * need) continue;
      const d = segSegDistance(segs[i], segs[j]);
      if (d < need) out.push({ i, j, d, arc });
    }
  }
  return out;
}
